import React from "react";
import BaseComponent, {executeComponentCallback} from 'Core/components/BaseComponent';
import {connect} from 'react-redux';
import {getPageActions} from 'Core/helpers/redux';
import SelectInput from 'Core/components/input/SelectInput';
import {find, get, isEqual, omit} from 'lodash';
import PropTypes from 'prop-types';
import * as actions from 'Pages/apps/default/customerGroups/actions';
import CountOptionComponent from 'Components/input/CustomerGroupSelectInput/options/countOption';
import CountSingleValueComponent from 'Components/input/CustomerGroupSelectInput/singleValues/countSingleValue';
import {CUSTOMER_GROUP_STATUS} from 'Pages/apps/default/customerGroups/const';

/**
 * Get all actions used by this component
 * @type {Object}
 */
const allActions = getPageActions(actions);

class CustomerGroupSelectInput extends BaseComponent {
	constructor(props) {
		super(props);

		this.state = {
			/** @type {CustomerGroupsListItemDataObject[]|{label: string, value: string}|null|undefined} */
			customerGroupList: null,
			/** @type {boolean} */
			customerGroupListLoading: false
		}

		// Data methods
		this.loadOptions = this.loadOptions.bind(this);
	}

	/** @inheritDoc */
	async asyncComponentDidMount() {
		await super.asyncComponentDidMount();

		// Load selects options
		await this.loadOptions();
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		// Re-load select options when project ID or client ID changes
		if (this.props.projectId !== prevProps.projectId) this.loadOptions().then();
	}

	/**
	 * Load select options
	 * @param {boolean} [onMount=false] - Specifies if this method is called as part of the component mount cycle.
	 * @return {Promise<void>}
	 */
	async loadOptions(onMount = false) {
		const {projectId, useDefaultOptionFormat, fetchCustomerGroupsListAction} = this.props;
		const executeAbortableAction = (onMount ? this.executeAbortableActionMount : this.executeAbortableAction);

		await this.setState({customerGroupListLoading: true})
			.then(() => executeAbortableAction(fetchCustomerGroupsListAction, projectId, undefined, 1, 999999))
			.then(res => get(res, 'data'))
			.then(
				/**
				 * @param {CustomerGroupsListItemDataObject[]|undefined} list
				 * @return {Promise<Object>}
				 */
				list => this.setState({
					customerGroupList: (
						useDefaultOptionFormat ?
							list.map(i => ({id: i.id, label: i.name, value: i.id})) :
							list
					)
				})
					// Update or clear the selected value depending on the freshly loaded list
					.then(() => {
						const {value} = this.props;
						
						// Do nothing if the value is not selected
						if (!value) return;
						
						const freshValue = find(list, {id: value.id});
						
						// Clear the selected value if it is not available in the freshly loaded list
						if (!freshValue) executeComponentCallback(this.props.onChange, null);
						// Update the selected value if it changed in the freshly loaded list
						else if (!isEqual(freshValue, value)) executeComponentCallback(this.props.onChange, freshValue);
					})
			)
			.finally(() => this.setState({customerGroupListLoading: false}));
	}

	render() {
		const {useDefaultOptionFormat} = this.props;
		const {customerGroupList, customerGroupListLoading} = this.state;

		return (
			<SelectInput
				{...omit(this.props, ['projectId', ...Object.keys(allActions)])}
				primaryKey={useDefaultOptionFormat ? 'value' : 'id'}
				options={!!customerGroupList ? customerGroupList : []}
				isLoading={customerGroupListLoading}
				getOptionValue={o => useDefaultOptionFormat ? o.value : o.id}
				getOptionLabel={o =>
					// (!!o.id && o.id !== '-1' ? `[${o.id}] ` : '') +
					(useDefaultOptionFormat ? o.label : o.name)
				}
				components={{Option: CountOptionComponent, SingleValue: CountSingleValueComponent}}
				isOptionDisabled = {o => (o.status !== CUSTOMER_GROUP_STATUS.ACTIVE)}
				onMenuOpen={() => this.loadOptions()}
			/>
		);
	}
}

/**
 * Define a component's own props that can be passed to it by parent components
 */
CustomerGroupSelectInput.propTypes = {
	...SelectInput.propTypes,

	projectId: PropTypes.string,
	useDefaultOptionFormat: PropTypes.bool,
};

/**
 * Define component default values for own props
 */
CustomerGroupSelectInput.defaultProps = {
	useDefaultOptionFormat: false,
};

export default connect(null, allActions, null, {forwardRef: true})(CustomerGroupSelectInput);